export interface FAQ {
  question: string;
  answer: string;
}

// Single source of truth for FAQ content, shared by FAQPage (all categories)
// and blog posts (category-matched subset). Keeping this in one place means
// a blog post and the FAQ page never give a visitor conflicting answers.

export const generalFaqs: FAQ[] = [
  {
    question: "How much does power washing cost in Ventura County?",
    answer: "Power washing costs vary based on the service and property size. Driveway cleaning typically starts at $200, house soft washing at $350, and window cleaning at $150. We provide free estimates for all Ventura County properties to give you an accurate quote tailored to your specific needs."
  },
  {
    question: "Do you offer free estimates?",
    answer: "Yes! We provide free, no-obligation estimates for all our services throughout Ventura County. Contact us by phone at (805) 464-7166 or use our online contact form to schedule your free estimate."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards, debit cards, cash, and checks. Payment is due upon completion of services. For larger commercial projects, we can discuss payment arrangements."
  },
  {
    question: "Are you licensed and insured?",
    answer: "Yes, we are fully licensed and carry comprehensive liability insurance to protect your property. Our team members are trained professionals who follow strict safety protocols on every job."
  },
  {
    question: "Do you use eco-friendly cleaning products?",
    answer: "Absolutely! We're committed to protecting Ventura County's beautiful environment. All our cleaning solutions are biodegradable, EPA-approved, and safe for your family, pets, and landscaping while still delivering powerful cleaning results."
  },
  {
    question: "What is your service area?",
    answer: "We serve all of Ventura County, including Camarillo, Fillmore, Moorpark, Oxnard, Port Hueneme, Santa Paula, Simi Valley, Thousand Oaks, and Ventura. Contact us if you're unsure whether we serve your area."
  },
  {
    question: "How do I schedule a service?",
    answer: "Scheduling is easy! You can call us directly at (805) 464-7166, fill out our online contact form, or request a free estimate through our website. We'll work with your schedule to find a convenient time."
  },
  {
    question: "What's your cancellation policy?",
    answer: "We understand plans change. We request at least 24 hours notice for cancellations or rescheduling. This allows us to accommodate other customers and maintain efficient service for everyone in Ventura County."
  }
];

export const pressureWashingFaqs: FAQ[] = [
  {
    question: "How often should I pressure wash my home in Ventura?",
    answer: "Due to Ventura County's coastal climate, we recommend pressure washing your home annually to prevent salt air damage and maintain your property's appearance."
  },
  {
    question: "Is pressure washing safe for all surfaces?",
    answer: "Our trained professionals use appropriate pressure levels for different surfaces. We adjust techniques for delicate materials like stucco, common in Ventura homes."
  },
  {
    question: "What's the difference between pressure washing and soft washing?",
    answer: "Pressure washing uses high-pressure water to clean hard surfaces like driveways and patios. Soft washing uses low pressure with specialized cleaning solutions for delicate surfaces like stucco, painted exteriors, and roofs."
  },
  {
    question: "How much does driveway pressure washing cost in Ventura?",
    answer: "Driveway pressure washing in Ventura typically ranges from $200-$400 depending on size and condition. We provide free estimates for all Ventura County properties."
  }
];

export const softWashFaqs: FAQ[] = [
  {
    question: "What's the difference between soft wash and pressure washing?",
    answer: "Soft wash uses low pressure with specialized cleaning solutions to safely clean delicate surfaces like stucco and painted exteriors, which are common in Ventura County homes."
  },
  {
    question: "Is soft washing safe for my Ventura home's landscaping?",
    answer: "Absolutely! Our eco-friendly soft wash solutions are designed to be safe for plants and landscaping, which is especially important for Ventura County's beautiful outdoor spaces."
  },
  {
    question: "How long do soft wash results last?",
    answer: "Soft wash treatments typically last 4-6 times longer than pressure washing because we eliminate organic growth at the source, making it perfect for Ventura's year-round growing season."
  },
  {
    question: "Is soft washing safe for my roof?",
    answer: "Yes! Soft washing is the recommended method for roof cleaning. High-pressure washing can damage shingles and tiles, while our gentle soft wash process safely removes moss, algae, and lichen without risking roof damage."
  }
];

export const windowFaqs: FAQ[] = [
  {
    question: "How often should I have my windows cleaned in Ventura?",
    answer: "Due to Ventura's coastal location, we recommend professional window cleaning every 2-3 months to combat salt air buildup and maintain crystal clear views."
  },
  {
    question: "Do you clean both interior and exterior windows?",
    answer: "Yes! We provide comprehensive window cleaning services for both interior and exterior surfaces, ensuring your Ventura property looks pristine inside and out."
  },
  {
    question: "What window cleaning methods do you use?",
    answer: "We use both traditional squeegee methods and pure water fed-pole systems for multi-story buildings. Our techniques ensure streak-free results on all window types."
  },
  {
    question: "Do you clean window screens and frames?",
    answer: "Yes, our comprehensive window cleaning service includes screen washing, frame cleaning, and sill detailing for complete window care."
  }
];

export const gutterFaqs: FAQ[] = [
  {
    question: "How often should I clean gutters in Ventura County?",
    answer: "Due to Ventura's oak trees and seasonal weather patterns, we recommend gutter cleaning twice yearly - once in late fall and once in early spring to handle leaf debris and winter runoff."
  },
  {
    question: "What happens if I don't clean my gutters regularly?",
    answer: "Clogged gutters can cause water overflow, leading to foundation damage, roof leaks, and landscape erosion - all costly problems in Ventura County's clay soil conditions."
  },
  {
    question: "Do you install gutter guards?",
    answer: "Yes! We install high-quality gutter protection systems that allow water flow while blocking leaves and debris. Gutter guards can significantly reduce maintenance needs for homes in heavily wooded areas."
  }
];

export const solarFaqs: FAQ[] = [
  {
    question: "How often should solar panels be cleaned in Ventura County?",
    answer: "Due to Ventura's dusty conditions and bird activity, we recommend cleaning solar panels every 3-6 months to maintain optimal efficiency and energy production."
  },
  {
    question: "Will dirty solar panels really affect my energy savings?",
    answer: "Yes! Studies show that dirty solar panels can lose 15-30% efficiency. In Ventura's sunny climate, regular cleaning ensures you're maximizing your solar investment."
  },
  {
    question: "Is it safe to clean solar panels myself?",
    answer: "We strongly recommend professional cleaning for safety and warranty protection. Our team uses proper equipment and techniques specifically designed for solar panels throughout Ventura County."
  },
  {
    question: "Do you offer bird deterrent installation for solar panels?",
    answer: "Yes! We offer humane bird deterrent installation and ongoing management services to prevent nesting under panels while maintaining your solar system's performance."
  }
];

export const carpetFaqs: FAQ[] = [
  {
    question: "How often should I clean my carpets in Ventura County?",
    answer: "We recommend professional carpet cleaning every 12-18 months for most Ventura homes. High-traffic areas or homes with pets may benefit from more frequent cleaning every 6-12 months."
  },
  {
    question: "How long does it take for carpets to dry?",
    answer: "With our professional equipment, most carpets in Ventura's climate dry within 6-8 hours. We use powerful extraction methods to remove maximum moisture during cleaning."
  },
  {
    question: "Can you remove pet stains and odors?",
    answer: "Yes! Our enzyme-based odor elimination treatments break down odor-causing bacteria at the molecular level, ensuring your carpets smell fresh and clean long after we've finished."
  },
  {
    question: "Is carpet cleaning safe for my family and pets?",
    answer: "Absolutely! We use non-toxic, eco-friendly cleaning solutions that are safe for your family and pets. Our deep extraction process also removes allergens and bacteria for a healthier home environment."
  }
];

export const dumpsterPadFaqs: FAQ[] = [
  {
    question: "How often should dumpster pads be cleaned?",
    answer: "Restaurants and food service properties should have dumpster enclosures cleaned monthly to control grease buildup and odor. Retail centers, apartment complexes, and HOAs typically do well with quarterly cleaning, though high-traffic areas may need more frequent service."
  },
  {
    question: "Where does the wastewater go?",
    answer: "We use professional containment and recovery equipment to capture wastewater during the cleaning process rather than letting it run into storm drains, keeping the service compliant with local water quality regulations."
  },
  {
    question: "Do you clean the dumpster itself or just the concrete pad?",
    answer: "Both. We degrease and pressure wash the interior and exterior of the dumpster along with the surrounding concrete pad and enclosure walls, removing grease stains, grime, and buildup from all surfaces."
  },
  {
    question: "Do you offer recurring service for businesses and HOAs?",
    answer: "Yes. Many of our commercial clients throughout Ventura County put dumpster pad cleaning on a recurring monthly or quarterly schedule so enclosures stay compliant and odor-free year-round."
  }
];

export const trustFaqs: FAQ[] = [
  {
    question: "How long have you been in business?",
    answer: "Ventura Power Washing has been proudly serving Ventura County for years, building a reputation for quality workmanship and excellent customer service throughout the community."
  },
  {
    question: "Do you offer any guarantees?",
    answer: "Yes! We stand behind our work with a satisfaction guarantee. If you're not completely satisfied with our service, contact us and we'll make it right."
  },
  {
    question: "Are your technicians trained and experienced?",
    answer: "All our technicians are fully trained professionals with extensive experience in exterior cleaning. They follow strict safety protocols and use the latest equipment and techniques."
  }
];

// Maps a blog post's `category` field to the most relevant FAQ set, so blog
// posts can carry a genuine, already-vetted FAQ block instead of either
// inventing new copy or shipping with none at all.
export const categoryFaqs: Record<string, FAQ[]> = {
  "Pressure Washing Tips": pressureWashingFaqs,
  "Soft Wash Services": softWashFaqs,
  "Window Cleaning": windowFaqs,
  "Gutter Maintenance": gutterFaqs,
  "Solar Panel Care": solarFaqs,
  "Carpet Care": carpetFaqs,
};

export function faqsForCategory(category?: string | null): FAQ[] {
  if (category && categoryFaqs[category]) return categoryFaqs[category];
  return generalFaqs.slice(0, 4);
}

// Maps a blog post's `category` field to the service page it's most
// relevant to, so a reader can jump straight from a how-to article to
// booking that exact service.
export const categoryServicePath: Record<string, { name: string; path: string }> = {
  "Pressure Washing Tips": { name: "Pressure Washing", path: "/services/pressure-washing" },
  "Soft Wash Services": { name: "Soft Wash", path: "/services/soft-wash" },
  "Window Cleaning": { name: "Window Cleaning", path: "/services/window-washing" },
  "Gutter Maintenance": { name: "Gutter Cleaning", path: "/services/gutter-cleaning" },
  "Solar Panel Care": { name: "Solar Panel Cleaning", path: "/services/solar-panel-cleaning" },
  "Carpet Care": { name: "Carpet Cleaning", path: "/services/carpet-cleaning" },
};

export function servicePathForCategory(category?: string | null) {
  if (category && categoryServicePath[category]) return categoryServicePath[category];
  return null;
}
